import { useState } from 'react';
import { motion } from 'framer-motion';
import ScrollReveal from './ScrollReveal';
import SocialLinks from './SocialLinks';
import { Mail, Phone, MapPin, Globe, ArrowRight, Loader2, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const WEB3FORMS_KEY = 'f53e9da6-bf77-4f3a-8fe7-226ecf87a079';

const ContactSection = () => {
  const { toast } = useToast();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = {
        access_key: WEB3FORMS_KEY,
        subject: 'Nuovo contatto dal sito Zofia',
        from_name: formState.name,
        name: formState.name,
        email: formState.email,
        company: formState.company || 'Non specificato',
        message: formState.message,
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormState({ name: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Errore invio form');
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore. Riprova più tardi.',
        variant: 'destructive'
      });
    }

    setIsSubmitting(false);
  };
  return <section id="contatti" className="section-padding relative overflow-hidden bg-navy/30">
      <div className="container-premium">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left content */}
          <div>
            <ScrollReveal>
              <p className="text-gold tracking-[0.2em] uppercase text-sm mb-6">
                Contatti
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={0.1}>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
                Iniziamo a lavorare insieme
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-muted-foreground mb-12">
                Raccontaci del tuo progetto. Ti risponderemo entro 24 ore 
                con una prima analisi gratuita.
              </p>
            </ScrollReveal>

            <div className="space-y-6">
              <ScrollReveal delay={0.3}>
                <a href="mailto:info@zofia.it" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <Mail className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-foreground link-underline">info@zofiadigitalagency.com</p>
                  </div>
                </a>
              </ScrollReveal>

              <ScrollReveal delay={0.4}>
                <a href="tel:+393294007889" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <Phone className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Telefono</p>
                    <p className="text-foreground link-underline">+39 329 400 7889</p>
                  </div>
                </a>
              </ScrollReveal>

              <ScrollReveal delay={0.5}>
                <a href="https://api.whatsapp.com/send?phone=393935697085&text=Ciao!%20Vorrei%20informazioni%20sui%20vostri%20servizi" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <MessageCircle className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">WhatsApp Business</p>
                    <p className="text-foreground link-underline">+39 393 569 7085</p>
                  </div>
                </a>
              </ScrollReveal>

              <ScrollReveal delay={0.6}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sede</p>
                    <p className="text-foreground">Campobasso, Italia</p>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.7}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Dove operiamo</p>
                    <p className="text-foreground font-medium">In tutta Italia</p>
                  </div>
                </div>
              </ScrollReveal>

              {/* Social Links */}
              <ScrollReveal delay={0.8}>
                <div className="pt-4 border-t border-border">
                  <SocialLinks variant="cta" />
                </div>
              </ScrollReveal>
            </div>
          </div>

          {/* Right form */}
          <ScrollReveal delay={0.3} direction="right">
            <div className="glass-card p-8 md:p-10">
              {isSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">✓</span>
                  </div>
                  <h3 className="font-serif text-2xl mb-4">Messaggio inviato!</h3>
                  <p className="text-muted-foreground">
                    Ti ricontatteremo entro 24 ore.
                  </p>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Nome *
                      </label>
                      <input type="text" id="name" required value={formState.name} onChange={e => setFormState({
                    ...formState,
                    name: e.target.value
                  })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-colors" placeholder="Il tuo nome" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email *
                      </label>
                      <input type="email" id="email" required value={formState.email} onChange={e => setFormState({
                    ...formState,
                    email: e.target.value
                  })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-colors" placeholder="email@azienda.it" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Azienda
                    </label>
                    <input type="text" id="company" value={formState.company} onChange={e => setFormState({
                  ...formState,
                  company: e.target.value
                })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-colors" placeholder="Nome azienda" />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Messaggio *
                    </label>
                    <textarea id="message" required rows={4} value={formState.message} onChange={e => setFormState({
                  ...formState,
                  message: e.target.value
                })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-colors resize-none" placeholder="Raccontaci del tuo progetto..." />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="btn-premium w-full flex items-center justify-center gap-2">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                        <span>Invia Messaggio</span>
                        <ArrowRight className="w-4 h-4" />
                      </>}
                  </button>

                  <p className="text-xs text-muted-foreground text-center">
                    Inviando accetti la nostra Privacy Policy
                  </p>
                </form>}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>;
};
export default ContactSection;